/**
 * usePrefetch Hook
 * Enables prefetching of lazy-loaded route modules on hover/focus.
 * Vite caches dynamic imports, so calling the factory multiple times
 * only results in a single network request.
 */

import { useCallback } from 'react'

type LazyFactory = () => Promise<unknown>

/**
 * Returns event handlers that trigger a dynamic import prefetch
 * when the user hovers or focuses a navigation element.
 *
 * Usage:
 *   const prefetchHandlers = usePrefetch(() => import('@/modules/accounting/features/balances'));
 *   <a {...prefetchHandlers} onClick={...}>Balances</a>
 */
export function usePrefetch(factory: LazyFactory): {
  onMouseEnter: () => void
  onFocus: () => void
} {
  const prefetch = useCallback(() => {
    // Fire-and-forget: Vite deduplicates concurrent calls to the same module
    factory().catch(() => {
      // Prefetch failures are non-fatal — the module will load on navigation
    })
  }, [factory])

  return {
    onMouseEnter: prefetch,
    onFocus: prefetch,
  }
}
